import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { formatDateTime, formatNumber, formatRM } from "@/lib/format";
import { PageHeader } from "@/components/portal/PortalLayout";
import { Card, ErrorNote, Loading } from "@/components/portal/Ui";

function Stat({ label, value, hint, to, testId, tone }) {
  const body = (
    <Card className="h-full transition-transform hover:-translate-y-0.5" testId={testId}>
      <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#8a8378]">{label}</p>
      <p className={`mt-2 text-3xl font-extrabold tabular ${tone === "warn" ? "text-[#A85A05]" : ""}`}>
        {value}
      </p>
      {hint && <p className="mt-1.5 text-xs leading-snug body-text">{hint}</p>}
    </Card>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export default function InternalOverview() {
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [o, a] = await Promise.all([
        api.get("/internal/overview"),
        api.get("/activity", { params: { limit: 25 } }),
      ]);
      setData(o.data);
      setActivity(a.data);
      setError("");
    } catch (e) {
      setError(apiError(e));
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  if (!data) return error ? <ErrorNote message={error} onRetry={load} /> : <Loading />;

  return (
    <div data-testid="internal-overview-page">
      <PageHeader
        title="Overview"
        description="Everything waiting on the AnyTribe team across your assigned campaigns."
        onRefresh={load}
        refreshing={refreshing}
      />
      <ErrorNote message={error} onRetry={load} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Active campaigns"
          value={formatNumber(data.active_campaigns)}
          hint={`${data.total_campaigns} campaigns in total`}
          to="/internal/campaigns"
          testId="stat-active-campaigns"
        />
        <Stat
          label="Assigned creators"
          value={formatNumber(data.assigned_creators)}
          hint="Creators with at least one assignment"
          to="/internal/campaigns"
          testId="stat-assigned-creators"
        />
        <Stat
          label="Published vs expected posts"
          value={`${formatNumber(data.published_posts)} / ${formatNumber(data.expected_posts)}`}
          hint="Across all assigned campaigns"
          to="/internal/performance"
          testId="stat-published-posts"
        />
        <Stat
          label="Missing posting links"
          value={formatNumber(data.missing_posting_links)}
          hint="Expected posts not yet published"
          tone={data.missing_posting_links ? "warn" : undefined}
          to="/internal/performance"
          testId="stat-missing-posts"
        />
        <Stat
          label="Missing insight submissions"
          value={formatNumber(data.missing_insights)}
          hint="Published posts with no insights yet"
          tone={data.missing_insights ? "warn" : undefined}
          to="/internal/performance?insight_status=missing"
          testId="stat-missing-insights"
        />
        <Stat
          label="Insights needing correction"
          value={formatNumber(data.insights_needing_correction)}
          hint="Excluded from client totals until resolved"
          tone={data.insights_needing_correction ? "warn" : undefined}
          to="/internal/performance?insight_status=needs_correction"
          testId="stat-needs-correction"
        />
        <Stat
          label="Invoices awaiting action"
          value={formatNumber(data.invoices_awaiting_action)}
          hint="Submitted and not yet approved"
          tone={data.invoices_awaiting_action ? "warn" : undefined}
          to="/internal/invoices?status=Submitted"
          testId="stat-invoices-awaiting"
        />
        <Stat
          label="Approved, unpaid"
          value={formatRM(data.approved_unpaid_sen)}
          hint={`Paid to date: ${formatRM(data.paid_sen)}`}
          to="/internal/invoices?status=Approved"
          testId="stat-approved-unpaid"
        />
        <Stat
          label="Creator applications"
          value={formatNumber(data.new_creator_applications)}
          hint="New or under review from the public site"
          tone={data.new_creator_applications ? "warn" : undefined}
          to="/internal/applications"
          testId="stat-creator-applications"
        />
      </div>

      <Card className="mt-5" testId="internal-activity-card">
        <h2 className="text-lg font-extrabold">Recent activity</h2>
        {activity.length === 0 ? (
          <p className="mt-3 text-sm body-text">No activity recorded yet.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {activity.map((item) => (
              <li key={item.id} className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-[#F5F1EA] pb-3 text-sm">
                <span className="font-semibold">{item.actor_name}</span>
                <span className="body-text">{item.action}</span>
                {item.detail && <span className="body-text">— {item.detail}</span>}
                <span className="ml-auto text-xs body-text">{formatDateTime(item.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
